import { useEffect, useState } from 'react';
import {
  listarClientes, syncClientes, verificarSituacao,
  atualizarConfigCliente, aplicarTemplateNotas, aplicarTemplateMapa, herdarReferencial,
  aberturaReceita, aberturasReceita,
} from '../api';
import { PageHeader, Loading, Empty, Alert, Badge } from '../components/common';
import { fmtCnpj, fmtCpf, fmtData, fmtDataHora } from '../lib/format';

const REGIMES = [
  { value: '',          label: '(usar regime do Gesthub)' },
  { value: 'simples',   label: 'Simples Nacional' },
  { value: 'presumido', label: 'Lucro Presumido' },
  { value: 'real',      label: 'Lucro Real' },
  { value: 'mei',       label: 'MEI' },
  { value: 'imune',     label: 'Imune/Isenta' },
];

export default function Clientes() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [regime, setRegime] = useState('');
  const [msg, setMsg] = useState(null);
  const [editando, setEditando] = useState(null);   // cliente em configuração
  const [buscandoAberturas, setBuscandoAberturas] = useState(false);

  const carregar = () => {
    setLoading(true);
    const filtros = {};
    if (busca) filtros.q = busca;
    if (regime) filtros.regime = regime;
    listarClientes(filtros)
      .then(r => setLista(r.items || []))
      .catch(err => setMsg({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [busca, regime]);

  const sync = async () => {
    setMsg({ tipo: 'info', txt: 'Sincronizando com Gesthub…' });
    try {
      const r = await syncClientes();
      setMsg({ tipo: 'success', txt: `${r.inseridos} inseridos · ${r.atualizados} atualizados · ${r.inalterados} inalterados (${r.duracao_ms}ms)` });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  const verificar = async (id) => {
    try {
      const r = await verificarSituacao(id);
      setMsg({ tipo: r.apto ? 'success' : 'warning', txt: `Cliente ${r.apto ? 'APTO' : 'INAPTO'}: ${r.motivo || 'situação ATIVA na Receita'}` });
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  // Preenche a data de abertura de quem esta sem, pela Receita. Demora: a
  // BrasilAPI limita a 5 req/s e o backend espaca as chamadas.
  const buscarAberturas = async () => {
    setBuscandoAberturas(true);
    setMsg({ tipo: 'info', txt: 'Consultando a Receita Federal — leva alguns segundos…' });
    try {
      const r = await aberturasReceita();
      const partes = [`${r.preenchidos} preenchida(s)`];
      if (r.divergentes)  partes.push(`${r.divergentes} divergente(s) do que estava cadastrado`);
      if (r.sem_resposta) partes.push(`${r.sem_resposta} sem resposta da Receita`);
      setMsg({ tipo: r.preenchidos ? 'success' : 'warning',
               txt: `${r.consultados} consultado(s): ${partes.join(' · ')}.` });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    } finally {
      setBuscandoAberturas(false);
    }
  };

  const semRegime   = lista.filter(c => !c.regime_efetivo && !c.regime).length;
  const semAbertura = lista.filter(c => !c.data_abertura).length;
  const semResponsavel = lista.filter(c => !c.contato_nome || !c.contato_cpf).length;

  return (
    <>
      <PageHeader
        title="Clientes"
        sub="Cadastro sincronizado do Gesthub + configuração local (regime, módulos, automações)"
        actions={<button onClick={sync}>↻ Sync agora</button>}
      />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}
      {semAbertura > 0 && (
        <Alert tipo="info">
          {semAbertura} cliente(s) sem data de abertura. Ela decide o primeiro exercício
          — e é o primeiro exercício que sai <strong>sem coluna comparativa</strong>, como
          manda o CPC 26 (R1). Sem a data, o sistema cai numa heurística de "exercício
          anterior zerado", que acerta na maioria dos casos mas não avisa quando erra.
          <button className="ghost" style={{ marginLeft: 10 }}
            disabled={buscandoAberturas} onClick={buscarAberturas}>
            {buscandoAberturas ? 'Consultando…' : '⟳ Buscar na Receita'}
          </button>
        </Alert>
      )}
      {semResponsavel > 0 && (
        <Alert tipo="info">
          {semResponsavel} cliente(s) sem responsável completo (nome e CPF). É ele que
          assina o balancete, a DRE e o balanço — sem o cadastro, o rodapé sai com a
          razão social e o CPF em branco, e só resta emitir com a assinatura do contador.
        </Alert>
      )}
      {semRegime > 0 && (
        <Alert tipo="warning">
          {semRegime} cliente(s) sem regime tributário definido — o menu fiscal aparece completo para eles.
          Defina o regime no Gesthub (fonte oficial) ou use o override local em ⚙ Configurar.
        </Alert>
      )}

      <div className="toolbar">
        <input placeholder="Buscar razão social ou CNPJ…" value={busca} onChange={e => setBusca(e.target.value)} />
        <select value={regime} onChange={e => setRegime(e.target.value)}>
          <option value="">Todos os regimes</option>
          <option value="simples">Simples Nacional</option>
          <option value="presumido">Lucro Presumido</option>
          <option value="real">Lucro Real</option>
          <option value="mei">MEI</option>
        </select>
      </div>

      <div className="card">
        {loading ? <Loading /> : lista.length === 0 ? (
          <Empty title="Sem clientes" sub="Clique em 'Sync agora' pra puxar do Gesthub." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Razão Social</th>
                <th>CNPJ</th>
                <th>Regime</th>
                <th>Abertura</th>
                <th>Responsável</th>
                <th>Módulos</th>
                <th>Automação</th>
                <th>Sincronizado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(c => (
                <tr key={c.id}>
                  <td>
                    <div className="bold">{c.razao_social}</div>
                    {c.nome_fantasia && <div className="small muted">{c.nome_fantasia}</div>}
                  </td>
                  <td className="small">{fmtCnpj(c.nr_insc)}</td>
                  <td>
                    <Badge tipo={c.regime_efetivo ? 'neutral' : 'warning'}>
                      {c.regime_efetivo || 'sem regime'}
                    </Badge>
                    {c.regime_override && <div className="small muted" title="Override local — Gesthub não tem o dado">override</div>}
                  </td>
                  <td className="small" style={{ whiteSpace: 'nowrap' }}>
                    {c.data_abertura
                      ? <span title="Data de constituição — define o primeiro exercício">
                          {fmtData(c.data_abertura)}
                        </span>
                      : <span className="muted" title="Sem data de abertura: o primeiro exercício é inferido">—</span>}
                  </td>
                  <td className="small">
                    {c.contato_nome
                      ? <>
                          <div>{c.contato_nome}</div>
                          {c.contato_cpf
                            ? <div className="small muted">{fmtCpf(c.contato_cpf)}</div>
                            : <div className="small muted" title="Sem CPF, a assinatura sai incompleta">CPF —</div>}
                        </>
                      : <span className="muted" title="Quem assina as demonstrações">—</span>}
                  </td>
                  <td className="small">
                    {[c.modulo_contabil && 'Contábil', c.modulo_fiscal && 'Fiscal', c.modulo_relatorios && 'Relatórios']
                      .filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="small">
                    {c.escrituracao_auto_notas && <Badge tipo="success">Notas ⚙</Badge>}{' '}
                    {c.escrituracao_auto_finance && <Badge tipo="success">Finance ⚙</Badge>}
                    {!c.escrituracao_auto_notas && !c.escrituracao_auto_finance && <span className="muted">manual</span>}
                  </td>
                  <td className="small muted">{fmtDataHora(c.sincronizado_em)}</td>
                  <td className="flex">
                    <button onClick={() => setEditando({ ...c })}>⚙ Configurar</button>
                    <button className="ghost" onClick={() => verificar(c.id)}>RFB</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editando && (
        <ConfigModal
          cliente={editando}
          onFechar={() => setEditando(null)}
          onSalvo={(novo, txt) => {
            setMsg({ tipo: 'success', txt });
            setEditando(null);
            carregar();
          }}
          onErro={(txt) => setMsg({ tipo: 'danger', txt })}
        />
      )}
    </>
  );
}

// =============================================================================
// Modal de configuração local do cliente
// =============================================================================
function ConfigModal({ cliente, onFechar, onSalvo, onErro }) {
  const [cfg, setCfg] = useState({
    regime_override:           cliente.regime_override || '',
    data_abertura:             (cliente.data_abertura || '').slice(0, 10),
    contato_nome:              cliente.contato_nome || '',
    contato_cpf:               cliente.contato_cpf || '',
    modulo_contabil:           cliente.modulo_contabil !== false,
    modulo_fiscal:             cliente.modulo_fiscal !== false,
    modulo_relatorios:         cliente.modulo_relatorios !== false,
    escrituracao_auto_notas:   !!cliente.escrituracao_auto_notas,
    escrituracao_auto_finance: !!cliente.escrituracao_auto_finance,
  });
  const [salvando, setSalvando] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [buscandoRfb, setBuscandoRfb] = useState(false);
  const [avisoRfb, setAvisoRfb] = useState(null);

  const set = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const salvar = async () => {
    setSalvando(true);
    try {
      await atualizarConfigCliente(cliente.id, {
        ...cfg,
        regime_override: cfg.regime_override || null,
        data_abertura:   cfg.data_abertura || null,
        contato_nome:    cfg.contato_nome.trim() || null,
        contato_cpf:     cfg.contato_cpf.replace(/\D/g, '') || null,
      });
      onSalvo(cfg, `Configuração de ${cliente.razao_social} salva.`);
    } catch (err) {
      onErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  // Busca na Receita e joga no campo — não grava. Quem grava é o Salvar,
  // então dá para conferir antes de aceitar o que a Receita devolveu.
  const buscarNaReceita = async () => {
    setBuscandoRfb(true); setAvisoRfb(null);
    try {
      const r = await aberturaReceita(cliente.id, true);
      if (r.data) {
        set('data_abertura', r.data);
        setAvisoRfb(r.anterior && r.anterior !== r.data
          ? `Receita: ${r.data} — estava ${r.anterior}. Confira antes de salvar.`
          : `Receita: ${r.data}.`);
      } else {
        setAvisoRfb('A Receita não devolveu data para este CNPJ.');
      }
    } catch (err) {
      setAvisoRfb('Falha na consulta: ' + err.message);
    } finally {
      setBuscandoRfb(false);
    }
  };

  // Pré-requisitos da escrituração automática: plano de contas + mapeamentos
  const prepararEscrituracao = async () => {
    setPreparando(true);
    try {
      await herdarReferencial(cliente.id).catch(() => {});  // idempotente; ignora se já tem plano
      const notas = await aplicarTemplateNotas(cliente.id);
      await aplicarTemplateMapa(cliente.id).catch(() => {});
      const resumo = (notas.items || []).map(i => `${i.tipo}: ${i.status}`).join(' · ');
      onSalvo(cfg, `Plano e mapeamentos preparados (${resumo}). Agora ative as automações e salve.`);
    } catch (err) {
      onErro(`Falha ao preparar: ${err.message}`);
    } finally {
      setPreparando(false);
    }
  };

  const Toggle = ({ campo, label, desc }) => (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
      <input type="checkbox" checked={cfg[campo]} onChange={e => set(campo, e.target.checked)} style={{ marginTop: 3 }} />
      <span>
        <span className="bold">{label}</span>
        {desc && <div className="small muted">{desc}</div>}
      </span>
    </label>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(8,8,10,0.75)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onFechar}>
      <div className="card" style={{ width: 560, maxHeight: '90vh', overflow: 'auto', padding: 20 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: 2 }}>⚙ {cliente.razao_social}</h3>
        <p className="small muted" style={{ marginBottom: 14 }}>
          Configuração local do Átrio Contábil — não altera o cadastro do Gesthub.
        </p>

        <div className="small bold" style={{ color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
          Regime tributário
        </div>
        <div className="small muted" style={{ marginBottom: 6 }}>
          Gesthub: <strong>{cliente.regime || 'não informado'}</strong>.
          O regime define o menu fiscal (Simples não vê PIS/COFINS nem IRPJ/CSLL).
        </div>
        <select
          value={cfg.regime_override}
          onChange={e => set('regime_override', e.target.value)}
          style={{ width: '100%', marginBottom: 16 }}
        >
          {REGIMES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>

        <div className="small bold" style={{ color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
          Data de abertura
        </div>
        <div className="small muted" style={{ marginBottom: 6 }}>
          Define o primeiro exercício. É por ela que o balanço e a DRE do ano de
          constituição saem <strong>sem coluna comparativa</strong> — não existe
          período anterior para comparar.
        </div>
        <div className="flex" style={{ gap: 8, marginBottom: avisoRfb ? 6 : 16 }}>
          <input type="date" value={cfg.data_abertura} style={{ flex: 1 }}
            onChange={e => set('data_abertura', e.target.value)} />
          <button className="ghost" disabled={buscandoRfb} onClick={buscarNaReceita}>
            {buscandoRfb ? 'Consultando…' : '⟳ Receita'}
          </button>
        </div>
        {avisoRfb && <div className="small muted" style={{ marginBottom: 16 }}>{avisoRfb}</div>}

        <div className="small bold" style={{ color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
          Responsável pela empresa
        </div>
        <div className="small muted" style={{ marginBottom: 6 }}>
          Quem assina o balancete, a DRE e o balanço. Sem isto o rodapé imprime a
          razão social e um CPF em branco.
        </div>
        <div className="flex" style={{ gap: 8, marginBottom: 16 }}>
          <input placeholder="Nome do responsável" style={{ flex: 2 }}
            value={cfg.contato_nome} onChange={e => set('contato_nome', e.target.value)} />
          <input placeholder="CPF" style={{ flex: 1 }} inputMode="numeric" maxLength={14}
            value={cfg.contato_cpf} onChange={e => set('contato_cpf', e.target.value)} />
        </div>

        <div className="small bold" style={{ color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
          Módulos visíveis
        </div>
        <Toggle campo="modulo_contabil"   label="Escrituração contábil" desc="Lançamentos, plano de contas, fechamento de período" />
        <Toggle campo="modulo_fiscal"     label="Fiscal" desc="Apurações (DAS/PIS-COFINS/IRPJ-CSLL) e obrigações acessórias" />
        <Toggle campo="modulo_relatorios" label="Relatórios gerenciais" desc="DRE, Balanço, Balancete, Razão, comparativos" />

        <div className="small bold" style={{ color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 2px' }}>
          Escrituração automática (workers diários)
        </div>
        <Toggle campo="escrituracao_auto_notas"
          label="Notas emitidas → lançamentos"
          desc="NFS-e do Átrio Notas viram rascunhos contábeis (D Clientes / C Receita) todo dia às 02:00" />
        <Toggle campo="escrituracao_auto_finance"
          label="Extrato Finance → lançamentos"
          desc="Transações classificadas do Átrio Finance viram rascunhos via mapeamento de categorias" />

        <div className="small muted" style={{ margin: '10px 0 16px', padding: '8px 10px', background: 'rgba(196,149,106,0.07)', borderRadius: 6 }}>
          Pré-requisito das automações: o cliente precisa de plano de contas e mapeamentos.
          <button className="ghost" style={{ marginLeft: 8 }} disabled={preparando} onClick={prepararEscrituracao}>
            {preparando ? 'Preparando…' : '⚒ Preparar plano + mapeamentos'}
          </button>
        </div>

        <div className="flex" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button className="ghost" onClick={onFechar}>Cancelar</button>
          <button className="primary" disabled={salvando} onClick={salvar}>
            {salvando ? 'Salvando…' : 'Salvar configuração'}
          </button>
        </div>
      </div>
    </div>
  );
}
